import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../USER/Auth/AuthContext';
import { usePermissions } from '../USER/Auth/PermissionsContext';

/**
 * ProtectedRoute — Protege rutas por autenticación, rol, nivel o permiso.
 *
 * Props:
 *  - requireAuth (bool, default true): exige sesión activa
 *  - minLevel (number): nivel mínimo requerido (1-5)
 *  - role (string): nombre de rol exacto requerido
 *  - permission (string): nombre de permiso granular requerido
 *  - redirectTo (string): ruta de redirección si no autenticado
 *  - denyRedirectTo (string): ruta de redirección si autenticado pero sin permisos (default '/home')
 */
function ProtectedRoute({
  children,
  permission = null,
  role = null,
  minLevel = null,
  requireAdmin = false,
  requireSuperAdmin = false,
  requireAuth = true,
  redirectTo = '/',
  denyRedirectTo = '/home',
}) {
  const { 
    currentUser, 
    role: authRole, 
    isDriver: authIsDriver, 
    isPassenger: authIsPassenger, 
    isAdmin: authIsAdmin, 
    isSuperAdmin: authIsSuperAdmin, 
    loading: authLoading 
  } = useAuth();
  const { 
    hasPermission, 
    hasRole, 
    hasMinLevel, 
    isAdmin: permsIsAdmin, 
    isSuperAdmin: permsIsSuperAdmin, 
    isDriver: permsIsDriver, 
    isPassenger: permsIsPassenger, 
    loading: permsLoading 
  } = usePermissions();

  const isAdmin = authIsAdmin || permsIsAdmin;
  const isSuperAdmin = authIsSuperAdmin || permsIsSuperAdmin;
  const isDriver = authIsDriver || permsIsDriver;
  const isPassenger = authIsPassenger || permsIsPassenger;

  const needsPermsCheck = permission !== null || role !== null || minLevel !== null || requireAdmin || requireSuperAdmin;

  if (authLoading || (needsPermsCheck && permsLoading)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  // 1. Sin sesión → redirigir al login
  if (requireAuth && !currentUser) {
    return <Navigate to={redirectTo} replace />;
  }

  // 2. Si se requiere SuperAdmin explícito
  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to={denyRedirectTo} replace />;
  }

  // 3. Si se requiere Admin explícito
  if (requireAdmin && !isAdmin && !isSuperAdmin) {
    return <Navigate to={denyRedirectTo} replace />;
  }

  // 4. Si no hay restricciones adicionales → permitir acceso
  if (!permission && !role && minLevel === null && !requireAdmin && !requireSuperAdmin) {
    return children;
  }

  // 5. Verificar nivel mínimo (Protección adicional: si minLevel >= 4 requiere ser Admin)
  if (minLevel !== null) {
    if (minLevel >= 4 && !isAdmin && !isSuperAdmin) {
      return <Navigate to={denyRedirectTo} replace />;
    }
    if (!hasMinLevel(minLevel) && !isAdmin && !isSuperAdmin) {
      return <Navigate to={denyRedirectTo} replace />;
    }
  }

  // 6. Verificar rol específico
  if (role) {
    const roleMatched =
      isAdmin ||
      isSuperAdmin ||
      hasRole(role) ||
      (role === 'driver' && isDriver) ||
      (role === 'passenger' && isPassenger) ||
      (role === 'admin' && (isAdmin || isSuperAdmin)) ||
      (role === 'super_admin' && isSuperAdmin) ||
      authRole === role;

    if (!roleMatched) {
      return <Navigate to={denyRedirectTo} replace />;
    }
  }

  // 7. Verificar permiso granular
  if (permission && !hasPermission(permission) && !isAdmin && !isSuperAdmin) {
    return <Navigate to={denyRedirectTo} replace />;
  }

  return children;
}

export default ProtectedRoute;
